import { createContext, useContext, useEffect, useState } from "react";
import { Sun, Moon, Waves, Leaf } from "lucide-react";

const ThemeContext = createContext(null);

export const THEMES = {
  light: {
    label: "Light",
    icon: <Sun className="w-5 h-5" />,
    vars: {
      "--bg": "#ffffff",
      "--text": "#000000",
      "--card": "#ebe8e8",
      "--border": "#000000ff",
      "--hover": "#2803faff",
      "--primary": "#1803d4ff",
      "--secondary": "#1310b9ff",
      "--accent": "rgb(230, 225, 222)",
      "--extra": "#a5430afa",
      "--add": "#850073ff",
      "--diff": "#fbff00",
      "--shadow": "0px 15px 15px 0px",
    },
    image: "/images/vips/vip4.jpg", // add image for theme
  },

  dark: {
    label: "Dark",
    icon: <Moon className="w-5 h-5" />,
    vars: {
      "--bg": "#000000",
      "--text": "#ffffff",
      "--card": "#080c13",
      "--border": "#ffffffff",
      "--hover": "#fbff00ff",
      "--primary": "#ffee00ff",
      "--secondary": "#ffd900ff",
      "--accent": "rgb(24, 4, 4)",
      "--extra": "#ff5e00fa",
      "--add": "#09ff00ff",
      "--diff": "#fbff00",
      "--shadow": "0 3px 3px 0",
    },
    image: "/images/vips/vip7.jpg",
  },

  ocean: {
    label: "Ocean",
    icon: <Waves className="w-5 h-5" />,
    vars: {
      "--bg": "rgb(6, 10, 248)",
      "--text": "#ffffff",
      "--card": "rgb(30, 34, 223)",
      "--border": "#ffffffff",
      "--hover": "#fbff00ff",
      "--primary": "rgb(255, 238, 0)",
      "--secondary": "rgb(255, 238, 0)",
      "--accent": "rgb(98, 0, 255)",
      "--extra": "#f38f88",
      "--add": "rgb(250, 235, 97)",
      "--diff": "#fbff00",
      "--shadow": "0 3px 3px 0",
    },
    image: "/images/vips/vip1.jpg",
  },

  emerald: {
    label: "Emerald",
    icon: <Leaf className="w-5 h-5" />,
    vars: {
      "--bg": "rgb(0, 255, 0)",
      "--text": "#000000",
      "--card": "rgb(21, 236, 13)",
      "--border": "#ffffffff",
      "--hover": "rgb(255, 255, 255)",
      "--primary": "rgb(25, 0, 255)",
      "--secondary": "rgb(0, 47, 255)",
      "--accent": "rgb(4, 131, 0)",
      "--extra": "#2b0505",
      "--add": "rgba(25, 0, 255, 0.74)",
      "--diff": "#fbff00",
      "--shadow": "0 3px 3px 0",
    },
    image: "/images/vips/vip14.jpg",
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return (
      localStorage.getItem("theme") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  useEffect(() => {
    const root = document.documentElement;
    const themeConfig = THEMES[theme];

    if (!themeConfig) return;

    // Prevent flash
    root.classList.add("no-transitions");

    // Apply CSS variables
    Object.entries(themeConfig.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    root.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);

    requestAnimationFrame(() => {
      root.classList.remove("no-transitions");
    });
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        themes: THEMES,
        image: THEMES[theme].image, // current theme image
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return context;
};

